import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { API } from '../App';
import { useT } from '../i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

const CLASSES = ['good', 'bad', 'unknown'];

/** Same shapes the server accepts (tls_fingerprint.parse_fingerprint): JA3 = 32 hex, JA4 = t13d…_…_…. */
export function looksLikeFingerprint(value) {
  const v = String(value || '').trim().toLowerCase();
  return /^[0-9a-f]{32}$/.test(v) || /^[a-z0-9]{10}_[0-9a-f]{12}_[0-9a-f]{12}$/.test(v);
}

/*
  TLS fingerprints: "Add fingerprint" — a JA3/JA4 hash with a mark, label and
  notes (POST /api/tls-fingerprints). The server normalises the hash and
  refuses a duplicate.
*/
export default function TlsAddFingerprint({ onCreated }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [fingerprint, setFingerprint] = useState('');
  const [classification, setClassification] = useState('bad');
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setFingerprint('');
    setClassification('bad');
    setLabel('');
    setNotes('');
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { fingerprint: fingerprint.trim(), classification };
      if (label.trim()) body.label = label.trim();
      if (notes.trim()) body.notes = notes.trim();
      const r = await axios.post(API + '/tls-fingerprints', body);
      toast.success(t('Fingerprint added'));
      if (onCreated) onCreated(r.data);
      setOpen(false);
      reset();
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.detail) || t('Could not add the fingerprint'));
    } finally {
      setSaving(false);
    }
  };

  const valid = looksLikeFingerprint(fingerprint);

  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="tls-add-open">
        <Plus className="w-4 h-4 mr-2" />
        {t('Add fingerprint')}
      </Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('Add fingerprint')}</DialogTitle>
            <DialogDescription>
              {t('Mark a JA3 or JA4 fingerprint before it shows up in your traffic. JA3 is 32 hex characters; JA4 looks like t13d1517h2_8daaf6152771_b186095e22b6.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="tls-add-hash">{t('Fingerprint hash')}</Label>
              <Input id="tls-add-hash" data-testid="tls-add-hash" className="font-mono" value={fingerprint} onChange={(e) => setFingerprint(e.target.value)} />
              {fingerprint && !valid ? <p className="text-xs text-destructive">{t('This is not a JA3 or JA4 fingerprint.')}</p> : null}
            </div>
            <div className="space-y-1">
              <Label>{t('Classification')}</Label>
              <Select value={classification} onValueChange={setClassification}>
                <SelectTrigger data-testid="tls-add-class"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CLASSES.map((c) => (<SelectItem key={c} value={c}>{t(c)}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="tls-add-label">{t('Label')}</Label>
              <Input id="tls-add-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Python requests" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="tls-add-notes">{t('Notes')}</Label>
              <Input id="tls-add-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>{t('Cancel')}</Button>
            <Button onClick={save} disabled={saving || !valid} data-testid="tls-add-save">{t('Save')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/*
  Built-in list (GET /api/tls-fingerprints/seed): what is classified out of the
  box, read only. Your own marks are added on top of it.
*/
export function TlsSeedList() {
  const { t } = useT();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(API + '/tls-fingerprints/seed')
      .then((r) => alive && setRows((r.data && Array.isArray(r.data.seed) && r.data.seed) || []))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <Card data-testid="tls-seed-card">
      <CardHeader>
        <CardTitle className="text-base">{t('Built-in fingerprint list')}</CardTitle>
        <CardDescription>{t('Classified out of the box, read only. Your marks are added on top of this list.')}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {error ? (
          <p className="p-4 text-sm text-destructive">{t('Could not load the built-in list.')}</p>
        ) : rows === null ? (
          <p className="p-4 text-sm text-muted-foreground">{t('Loading…')}</p>
        ) : (
          <Table data-testid="tls-seed-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('Fingerprint')}</TableHead>
                <TableHead>{t('Label')}</TableHead>
                <TableHead>{t('Classification')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.fingerprint}>
                  <TableCell className="font-mono text-xs">{r.fingerprint}</TableCell>
                  <TableCell className="text-sm">{r.label || '—'}</TableCell>
                  <TableCell><Badge variant="outline">{t(r.classification || 'unknown')}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
